import { Controller } from '@hotwired/stimulus'
import { addToArray, removeFromArray } from '~/utils/array'
import { Turbo } from '@hotwired/turbo-rails'

export default class extends Controller {
  static values = {
    selectedAges: { type: Array, default: [] },
    selectedActivities: { type: Array, default: [] },
    selectedDates: { type: Array, default: [] },
    selectedOptions: { type: Array, default: [] },
    selectedPrice: { type: Array },
    activitiesRangePrice: { type: Array },
    selectedDistance: { type: String } // String because it can be 'unlimited_distance'
  }

  static targets = [
    'selectedDates',
    'selectedAges',
    'selectedActivities',
    'selectedOptions',
    'selectedDistance',
    'selectedPrice',
    'hideSoldOutDesktopInput',
    'hideSoldOutMobileInput'
  ]

  connect () {
    this.selectedPriceChanged()
  }

  selectedAgesValueChanged (value) {
    this.selectedAgesTargets.map((element) => {
      if (value.length > 0) {
        element.parentElement.classList.add('text-blue-400')
      } else {
        element.parentElement.classList.remove('text-blue-400')
      }
    })
  }

  selectedPriceChanged () {
    if (this.#arraysEqual(this.selectedPriceValue, this.activitiesRangePriceValue)) {
      this.selectedPriceTarget.parentElement.classList.remove('text-blue-400')
    } else {
      this.selectedPriceTarget.parentElement.classList.add('text-blue-400')
    }
  }

  #arraysEqual (a, b) {
    if (a.length !== b.length) return false

    for (let i = 0; i < a.length; i++) {
      if (a[i] !== b[i]) return false
    }

    return true
  }

  selectedActivitiesValueChanged (value) {
    this.selectedActivitiesTargets.map((element) => {
      if (value.length > 0) {
        element.parentElement.classList.add('text-blue-400')
      } else {
        element.parentElement.classList.remove('text-blue-400')
      }
    })
  }

  selectedOptionsValueChanged (value) {
    this.selectedOptionsTargets.map((element) => {
      if (value.length > 0) {
        element.parentElement.classList.add('text-blue-400')
      } else {
        element.parentElement.classList.remove('text-blue-400')
      }
    })
  }

  selectedDatesValueChanged (value) {
    this.selectedDatesTargets.map((element) => {
      if (value.length > 0) {
        element.parentElement.classList.add('text-blue-400')
      } else {
        element.parentElement.classList.remove('text-blue-400')
      }
    })
  }

  selectedDistanceValueChanged (value) {
    const element = this.selectedDistanceTarget
    if (value === 'unlimited_distance' || Number(value)) {
      element.parentElement.classList.add('text-blue-400')
    } else {
      element.parentElement.classList.remove('text-blue-400')
    }
  }

  updatePriceValue(event) {
    // Receives the custom event from the price-slider controller
    if (event.detail && event.detail.minPrice !== undefined && event.detail.maxPrice !== undefined) {
      const minPrice = event.detail.minPrice;
      const maxPrice = event.detail.maxPrice;

      // Validate that they are valid numbers
      if (!isNaN(minPrice) && !isNaN(maxPrice)) {
        this.selectedPriceValue = [minPrice, maxPrice];
        this.selectedPriceChanged();
        // Don't submit automatically, just update the values
      }
    }
  }

  clearDates () {
    this.selectedDatesValue = []
  }

  setAges (e) {
    const value = e.target.value;

    if (e.target?.checked) {
      this.selectedAgesValue = addToArray(this.selectedAgesValue, value)
    } else {
      this.selectedAgesValue = removeFromArray(this.selectedAgesValue, value)
    }
  }

  setActivities (e) {
    const value = e.target.value;

    if (e.target?.checked) {
      this.selectedActivitiesValue = addToArray(this.selectedActivitiesValue, value)
    } else {
      this.selectedActivitiesValue = removeFromArray(this.selectedActivitiesValue, value)
    }
  }

  setOptions (e) {
    const value = e.target.value;

    if (e.target?.checked) {
      this.selectedOptionsValue = addToArray(this.selectedOptionsValue, value)
    } else {
      this.selectedOptionsValue = removeFromArray(this.selectedOptionsValue, value)
    }
  }

  setDates (e) {
    const { start, end } = e.detail

    const startDate = start.format('MMM, DD')
    const endDate = end.format('MMM, DD')

    this.selectedDatesValue = [startDate, endDate]
  }

  #constructAndSubmitParams (formElement) {
    let baseUrl = formElement.action;
    const questionMarkIndex = baseUrl.indexOf('?');
    if (questionMarkIndex !== -1) {
      baseUrl = baseUrl.substring(0, questionMarkIndex);
    }

    const params = new URLSearchParams()

    // Explicitly search for price values in the inputs
    const priceSliderElement = document.querySelector('[data-controller="price-slider"]');
    if (priceSliderElement) {
      const minPriceInput = priceSliderElement.querySelector('[data-price-slider-target="minPrice"]');
      const maxPriceInput = priceSliderElement.querySelector('[data-price-slider-target="maxPrice"]');
      const rangeMinPrice = priceSliderElement.querySelector('[data-price-slider-target="activitiesMinPrice"]');
      const rangeMaxPrice = priceSliderElement.querySelector('[data-price-slider-target="activitiesMaxPrice"]');

      if (minPriceInput && maxPriceInput && rangeMinPrice && rangeMaxPrice) {
        const minPrice = parseInt(minPriceInput.value);
        const maxPrice = parseInt(maxPriceInput.value);
        const activitiesMinPrice = parseInt(rangeMinPrice.value);
        const activitiesMaxPrice = parseInt(rangeMaxPrice.value);

        // Update values in the controller
        this.selectedPriceValue = [minPrice, maxPrice];
        this.activitiesRangePriceValue = [activitiesMinPrice, activitiesMaxPrice];
      }
    }

    // Text Query
    const queryInput = formElement.querySelector('[name="query"]')
    if (queryInput && queryInput.value) {
      params.append('query', queryInput.value)
    }

    // All Ages (if exists as an input and is checked)
    const allAgesInput = formElement.querySelector('[name="all_ages"]')
    if (allAgesInput && allAgesInput.checked) {
      params.append('all_ages', allAgesInput.value || '1')
    }

    // Selected Ages (selectedAgesValue)
    if (this.selectedAgesValue && this.selectedAgesValue.length > 0) {
      this.selectedAgesValue.forEach(age => {
        params.append('ages[]', age);
      })
    }

    // Selected Activities (selectedActivitiesValue)
    if (this.selectedActivitiesValue && this.selectedActivitiesValue.length > 0) {
      this.selectedActivitiesValue.forEach(activity => {
        params.append('interests_text[]', activity);
      });
    }

    // Dates (started_at, finished_at)
    if (this.selectedDatesValue && this.selectedDatesValue.length === 2) {
      const startDate = this.selectedDatesValue[0]
      const endDate = this.selectedDatesValue[1]
      if (startDate) {
        params.append('started_at', startDate)
      }
      if (endDate) {
        params.append('finished_at', endDate)
      }
    }

    // Selected Options (selectedOptionsValue)
    if (this.selectedOptionsValue && this.selectedOptionsValue.length > 0) {
      this.selectedOptionsValue.forEach(option => {
        params.append('activity_options[]', option);
      });
    }

    // Price (selectedPriceValue, activitiesRangePriceValue)
    // Check if selectedPriceValue has the expected structure [min, max]
    if (this.selectedPriceValue && this.selectedPriceValue.length === 2 &&
        this.activitiesRangePriceValue && this.activitiesRangePriceValue.length === 2) {
      // Only send values if they are different from the default range of all activities
      if (!this.#arraysEqual(this.selectedPriceValue, this.activitiesRangePriceValue)) {
        const minPrice = this.selectedPriceValue[0];
        const maxPrice = this.selectedPriceValue[1];

        if (minPrice !== undefined && minPrice !== null) {
          params.append('min_price', minPrice);
        }

        if (maxPrice !== undefined && maxPrice !== null) {
          params.append('max_price', maxPrice);
        }
      }
    } else {
      // Fallback: get values directly from min_price and max_price fields in the form
      const minPriceInput = formElement.querySelector('[name="min_price"]')
      const maxPriceInput = formElement.querySelector('[name="max_price"]')

      if (minPriceInput && minPriceInput.value) {
        params.append('min_price', minPriceInput.value)
      }

      if (maxPriceInput && maxPriceInput.value) {
        params.append('max_price', maxPriceInput.value)
      }
    }

    // Distance (selectedDistanceValue)
    if (this.selectedDistanceValue &&
        typeof this.selectedDistanceValue === 'string' &&
        this.selectedDistanceValue.trim() !== '' &&
        this.selectedDistanceValue.trim() !== '[]' &&
        this.selectedDistanceValue !== 'unlimited_distance') {
      params.append('distance', this.selectedDistanceValue.trim());
    } else if (this.selectedDistanceValue === 'unlimited_distance') {
      // If distance is unlimited, you may not want to send the 'distance' parameter
      // or send a specific value that the backend understands as unlimited.
      // For now, if it's 'unlimited_distance', nothing is added, adjust according to backend logic.
    }

    // Hide Sold Out (hide_sold_out)
    if (this.hasHideSoldOutDesktopInputTarget && this.hideSoldOutDesktopInputTarget.checked) {
      params.append('hide_sold_out', '1');
    }

    // Location (address, longitude, latitude)
    const addressInput = formElement.querySelector('[name="address"]')
    if (addressInput && addressInput.value) {
      params.append('address', addressInput.value)
    }
    const longitudeInput = formElement.querySelector('[name="longitude"]')
    if (longitudeInput && longitudeInput.value) {
      params.append('longitude', longitudeInput.value)
    }
    const latitudeInput = formElement.querySelector('[name="latitude"]')
    if (latitudeInput && latitudeInput.value) {
      params.append('latitude', latitudeInput.value)
    }

    // Geo Bounding Box coordinates
    const neLatInput = formElement.querySelector('[name="ne_lat"]')
    const neLngInput = formElement.querySelector('[name="ne_lng"]')
    const swLatInput = formElement.querySelector('[name="sw_lat"]')
    const swLngInput = formElement.querySelector('[name="sw_lng"]')

    if (neLatInput?.value) params.append('ne_lat', neLatInput.value)
    if (neLngInput?.value) params.append('ne_lng', neLngInput.value)
    if (swLatInput?.value) params.append('sw_lat', swLatInput.value)
    if (swLngInput?.value) params.append('sw_lng', swLngInput.value)

    const queryString = params.toString();
    const finalUrl = queryString ? `${baseUrl}?${queryString}` : baseUrl

    Turbo.visit(finalUrl)
  }

  _handleFormSubmit(event) {
    event.preventDefault();
    event.stopImmediatePropagation(); // To ensure that other submit listeners don't fire
    this.#constructAndSubmitParams(event.target); // event.target here is the form
  }

  submit (event) {
    // This method is probably called by data-actions on individual inputs (e.g., change->filter#submit)
    event.preventDefault();

    const currentInput = event.target

    // If the event comes from the "Next" button in the price modal, make sure to capture the current values
    if (currentInput.type === 'button' && currentInput.innerText === 'Apply') {
      // Find price values in the inputs
      const priceSliderElement = document.querySelector('[data-controller="price-slider"]');
      if (priceSliderElement) {
        const minPriceInput = priceSliderElement.querySelector('[data-price-slider-target="minPrice"]');
        const maxPriceInput = priceSliderElement.querySelector('[data-price-slider-target="maxPrice"]');

        if (minPriceInput && maxPriceInput) {
          const minPrice = parseInt(minPriceInput.value);
          const maxPrice = parseInt(maxPriceInput.value);
          this.selectedPriceValue = [minPrice, maxPrice];
        }
      }
    }

    // Sync 'hide_sold_out' checkboxes
    if (currentInput.name === 'hide_sold_out') {
      if (this.hasHideSoldOutDesktopInputTarget && this.hideSoldOutDesktopInputTarget !== currentInput) {
        this.hideSoldOutDesktopInputTarget.checked = currentInput.checked
      }
      if (this.hasHideSoldOutMobileInputTarget && this.hideSoldOutMobileInputTarget !== currentInput) {
        this.hideSoldOutMobileInputTarget.checked = currentInput.checked
      }
    }

    const form = currentInput.closest('form')
    if (form) {
      this.#constructAndSubmitParams(form)
    } else {
      // Try to find the form based on the controller element as a fallback
      const controllerForm = this.element.closest('form')
      if (controllerForm) {
        this.#constructAndSubmitParams(controllerForm)
      }
    }
  }
}
